import React, { useEffect, useState } from 'react';

import { ReactComponent as Arrow } from '../../../images/icons/arrow.svg';
import ContentfulImage from '../../Common/ContentfulImage';
import { PrimaryLink } from '../../Common/Links';
import SmartLink from '../../Common/SmartLink';
import BeefSammy from './BeefSammy';
import * as styles from './Header.module.scss';
import * as ms from './MobileNav.module.scss';
import OptionalCard from './OptionalCard';

const MobileNav = ({ items }) => {
  const [state, setState] = useState({
    navOpen: false,
    subOpen: '',
  });

  useEffect(() => {
    if (!state.navOpen) document.body.removeAttribute('style');
  }, []);

  const toggleNav = () => {
    document.body.setAttribute('style', state.navOpen ? '' : 'overflow: hidden');

    // Reset nav state (primary vs. secondary menus) upon closing
    setState((prev) => ({ navOpen: !prev.navOpen, subOpen: !prev.navOpen ? '' : prev.subOpen }));
  };

  const toggleSubNav = (title) => {
    setState({ ...state, subOpen: title });
  };

  const menu = items.map((item, i) => {
    const subNav = item.menuItems;

    return (
      <li key={i} className={[styles.menuItem, ms.menuItem].join(' ')}>
        {item.url && (
          <SmartLink
            tabIndex={!state.navOpen ? '-1' : '0'}
            className={[styles.menuItemLink, ms.menuItemLink].join(' ')}
            to={item.url}
          >
            {item.title}
          </SmartLink>
        )}
        {!item.url && (
          <div className={[styles.menuItemLink, ms.menuItemLink, ms.grid].join(' ')}>
            {item.titleUrl ? (
              <SmartLink
                to={item.titleUrl}
                tabIndex={!state.navOpen ? '-1' : '0'}
                className={[ms.btnText, ms.highLevelLink].join(' ')}
              >
                {item.title}
              </SmartLink>
            ) : (
              <span className={ms.btnText}>{item.title}</span>
            )}
            <button
              aria-label={'Open sub-nav'}
              className={ms.openArrow}
              tabIndex={!state.navOpen ? '-1' : '0'}
              onClick={() => toggleSubNav(item.title)}
            >
              <Arrow />
            </button>
          </div>
        )}

        {subNav && (
          <ul
            className={[ms.subNav, 'unstyled-list'].join(' ')}
            aria-hidden={item.title !== state.subOpen}
            data-active={item.title === state.subOpen}
          >
            <li className={[styles.menuItem, ms.menuItem].join(' ')}>
              <button
                tabIndex={item.title !== state.subOpen ? '-1' : '0'}
                className={[styles.menuItemLink, ms.menuItemLink, ms.close].join(' ')}
                onClick={() => toggleSubNav('')}
              >
                <span className={ms.closeArrow}>
                  <Arrow />
                </span>
                <span className={ms.btnText}>Back</span>
              </button>
            </li>

            {subNav.map((sub, s) => {
              if (sub.__typename === 'ContentfulComponentMenuItem') {
                // Individual menu items
                if (!sub.subMenuItems) {
                  return (
                    <li className={[styles.menuItem, ms.menuItem].join(' ')} key={s}>
                      <div className={ms.linkList}>
                        {sub.icon && <ContentfulImage className={styles.svgImg} imageFragment={sub.icon} />}
                        {sub.url ? (
                          <SmartLink
                            tabIndex={item.title !== state.subOpen ? '-1' : '0'}
                            to={sub.url}
                            className={[styles.menuItemLink, ms.text].join(' ')}
                          >
                            {sub.title}
                            {sub.description && (
                              <p className={[styles.itemDesc, ms.text].join(' ')}>{sub.description}</p>
                            )}
                          </SmartLink>
                        ) : (
                          <div className={[styles.menuItemLink, ms.text].join(' ')}>
                            {sub.title}
                            {sub.description && (
                              <p className={[styles.itemDesc, ms.text].join(' ')}>{sub.description}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                } else {
                  return (
                    <li className={[styles.menuItem, ms.menuItem].join(' ')} key={s}>
                      {!sub.url ? (
                        <span className={[styles.menuItemLink, ms.menuItemLink].join(' ')}>
                          {sub.icon && <ContentfulImage className={styles.svgImg} imageFragment={sub.icon} />}
                          {sub.title}
                        </span>
                      ) : (
                        <SmartLink
                          tabIndex={item.title !== state.subOpen ? '-1' : '0'}
                          to={sub.url}
                          className={[styles.menuItemLink, ms.menuItemLink].join(' ')}
                        >
                          {sub.icon && <ContentfulImage className={styles.svgImg} imageFragment={sub.icon} />}
                          <div>
                            {sub.title}
                            {sub.descriptionText && (
                              <p className={[styles.itemDesc, ms.text].join(' ')}>{sub.descriptionText}</p>
                            )}
                          </div>
                        </SmartLink>
                      )}
                      {sub.subMenuItems && (
                        <ul className={'unstyled-list'}>
                          {sub.subMenuItems.map((child, c) => {
                            return (
                              <li className={[styles.menuItem, ms.menuItem].join(' ')} key={c}>
                                <div className={ms.linkRow}>
                                  <SmartLink
                                    tabIndex={item.title !== state.subOpen ? '-1' : '0'}
                                    to={child.url}
                                    className={[styles.menuItemLink, ms.text].join(' ')}
                                  >
                                    <div>
                                      {child.title}
                                      {child.description && (
                                        <p className={[styles.itemDesc, ms.text].join(' ')}>{child.description}</p>
                                      )}
                                    </div>
                                  </SmartLink>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                }
              } else {
                // Additional menus with their own individual menu items
                return (
                  <li className={[styles.menuItem, ms.menuItem].join(' ')} key={s}>
                    <div className={ms.linkList}>
                      {sub.titleUrl ? (
                        <SmartLink
                          tabIndex={item.title !== state.subOpen ? '-1' : '0'}
                          to={sub.titleUrl}
                          className={[styles.menuItemLink, ms.text].join(' ')}
                        >
                          {sub.title}
                          {sub.description?.description && (
                            <p className={[styles.itemDesc, ms.text].join(' ')}>{sub.description?.description}</p>
                          )}
                        </SmartLink>
                      ) : (
                        <div className={[styles.menuItemLink, ms.text].join(' ')}>
                          {sub.title}
                          {sub.description?.description && (
                            <p className={[styles.itemDesc, ms.text].join(' ')}>{sub.description?.description}</p>
                          )}
                        </div>
                      )}
                    </div>

                    <ul className={'unstyled-list'}>
                      {sub.menuItems.map((child, c) => {
                        return (
                          <li className={[styles.menuItem, ms.menuItem].join(' ')} key={c}>
                            <div className={ms.linkRow}>
                              <SmartLink
                                tabIndex={item.title !== state.subOpen ? '-1' : '0'}
                                to={child.url}
                                className={[styles.menuItemLink, ms.text].join(' ')}
                              >
                                {child.icon && <ContentfulImage className={styles.svgImg} imageFragment={child.icon} />}
                                <div>
                                  {child.title}
                                  {child.descriptionText && (
                                    <p className={[styles.itemDesc, ms.text].join(' ')}>{child.descriptionText}</p>
                                  )}
                                </div>
                              </SmartLink>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              }
            })}
            {item.optionalCard && <OptionalCard optionalCard={item.optionalCard} />}
          </ul>
        )}
      </li>
    );
  });

  return (
    <>
      <nav className={ms.mobileNav} aria-hidden={!state.navOpen} data-active={state.navOpen}>
        <div className={ms.container}>
          <ul className={[ms.mainNav, 'unstyled-list'].join(' ')}>{menu}</ul>
          {state.subOpen === '' && (
            <ul className={[styles.ctaNav, ms.ctaNav, 'unstyled-list'].join(' ')}>
              <>
                <li>
                  <SmartLink tabIndex={!state.navOpen ? '-1' : '0'} to="/ui">
                    Login
                  </SmartLink>
                </li>
                <li>
                  <PrimaryLink tabIndex={!state.navOpen ? '-1' : '0'} url="/demo" isButton label="Get a demo" />
                </li>
              </>
            </ul>
          )}
        </div>
      </nav>

      <BeefSammy onClick={toggleNav} open={state.navOpen} />
    </>
  );
};

export default MobileNav;
